import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFi from '@angular/common/locales/fi';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  provideClientHydration,
  withEventReplay,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideLottieOptions } from 'ngx-lottie';
import * as Sentry from '@sentry/angular';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { routes } from './app.routes';
import { erpAuthInterceptor } from './core/interceptors/erp-auth.interceptor';
import { WebshopSessionService } from './core/services/webshop-session.service';
import { AnalyticsService } from './core/services/analytics.service';
import { SeoService } from './core/services/seo.service';

registerLocaleData(localeFi);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /*
       * Off during the build-time prerender pass, on in every browser.
       *
       * The prerendered pages are rendered by Node with no visitor, no session cookie and no
       * business asking the ERP anything. A query left enabled there does not merely fetch
       * pointlessly: the renderer waits for the app to go stable, so one live ERP round trip per
       * prerendered route would sit in the middle of every build — and a slow or unreachable ERP
       * would stall it until the 30-second render timeout and fail the deploy.
       *
       * `typeof window` rather than `isBrowserPlatform()` because this runs at module load, with
       * no injector to ask. Queries that set `enabled` themselves override this, so anything added
       * to a prerendered page has to keep the same rule in mind.
       */
      enabled: typeof window !== 'undefined',
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    /*
     * Hydration: the client takes over the prerendered DOM instead of throwing it away and
     * building it again. Without this the prerender was pure cost at load time — the page painted
     * as static HTML, then went blank for as long as the bundles and the ERP took, then painted
     * the same thing again; on the front page that was a nine-second render delay on the LCP.
     *
     * Two conditions keep it honest. The first render in the browser has to produce the same DOM
     * the build did, which is why the session initialiser below no longer holds bootstrap: with
     * no session the header renders signed-out, exactly as the build rendered it, and the signals
     * fill in afterwards. And `withNoHttpTransferCache`: the build makes no ERP requests, but if
     * it ever did, a response cached into HTML every visitor shares would be someone's session.
     * `withEventReplay` keeps the clicks a visitor makes before the app is interactive.
     */
    provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideHttpClient(withXhr(), withInterceptors([erpAuthInterceptor])),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
    provideTanStackQuery(queryClient),
    { provide: LOCALE_ID, useValue: 'fi' },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    // Temporarily disabled for now, add back when we figure out billing:
    // {
    //   provide: Sentry.TraceService,
    //   deps: [Router],
    // },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: () => () => void 0,
    //   deps: [Sentry.TraceService],
    //   multi: true,
    // },
    {
      /*
       * Started here, not awaited here. The ERP round trip used to gate the first render of
       * every page — content pages that need no session included — and a first render that
       * depends on its answer cannot hydrate the prerendered markup (see above). Everything
       * that must know who is looking before it acts waits on `whenReady()` instead, which
       * resolves from this same request.
       */
      provide: APP_INITIALIZER,
      useFactory: (sessionService: WebshopSessionService) => () => {
        void sessionService.initialize();
      },
      deps: [WebshopSessionService],
      multi: true,
    },
    {
      // Likewise: `init()` waits for the session itself, and nothing rendered depends on it.
      provide: APP_INITIALIZER,
      useFactory: (analyticsService: AnalyticsService) => () => {
        void analyticsService.init();
      },
      deps: [AnalyticsService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (seoService: SeoService) => () => seoService.init(),
      deps: [SeoService],
      multi: true,
    },
  ],
};
